/** The policy's one stop slot: a draft, a request awaiting validation and a scheduled stop share it. */
export const openStopBranch = <T extends { branchType?: string; branchStatus?: string }>(
  branches?: T[],
): T | undefined =>
  branches?.find(
    ({ branchType, branchStatus }) => branchType === 'STOP' && branchStatus === 'BRANCH_OPEN',
  );

/**
 * Which branch is holding the stop up. Policy-level "is this policy's stop awaiting validation"
 * is `policy.isStopAwaitingValidation`, stamped by the backend — this answers the narrower
 * question the events timeline asks, and the list response carries no branches to ask it of.
 */
export const isStopBranchAwaitingValidation = (branch: {
  branchType?: string;
  branchStatus?: string;
  derogations?: Array<{ status: string }>;
}): boolean =>
  branch.branchType === 'STOP' &&
  branch.branchStatus === 'BRANCH_OPEN' &&
  (branch.derogations?.some(({ status }) => status === 'CREATED') ?? false);

export type StopState = 'draft' | 'awaitingValidation' | 'scheduled';

/** Which of the three states the open stop slot holds: only the BASE echo separates the last two. */
export const stopState = ({
  branch,
  baseEchoesStop,
}: {
  branch: Parameters<typeof isStopBranchAwaitingValidation>[0];
  baseEchoesStop: boolean;
}): StopState => {
  if (isStopBranchAwaitingValidation(branch)) {
    return 'awaitingValidation';
  }

  return baseEchoesStop ? 'scheduled' : 'draft';
};

/** A stop request a manager refused: the branch is closed and the request reverted to a draft. */
export const isStopDerogationDenied = (branch: {
  branchType?: string;
  derogations?: Array<{ status: string }>;
}): boolean =>
  branch.branchType === 'STOP' &&
  (branch.derogations?.some(({ status }) => status === 'DENIED') ?? false);

type StopBranch = {
  id?: string;
  branchType?: string;
  branchStatus?: string;
  parentBranchId?: string;
  derogations?: Array<{ status: string }>;
};

/**
 * The branch a denial closed, found from the draft it forked open. Nothing on the fork itself
 * marks it as refused — the denial lives on its parent, which is the only way to tell a refused
 * request from a draft nobody ever submitted.
 */
export const findStopRefusal = (
  draftBranchId: string | undefined,
  branches: StopBranch[] | undefined,
): StopBranch | undefined => {
  const draft = branches?.find(({ id }) => id === draftBranchId);

  if (!draft?.parentBranchId) {
    return undefined;
  }

  const parent = branches?.find(({ id }) => id === draft.parentBranchId);

  return parent && isStopDerogationDenied(parent) ? parent : undefined;
};

/**
 * Mirrors the backend: a wholesaler may call any stop off; otherwise whoever may request the reason
 * may, and a reason naming no role falls back to the requester and the policy's brokers.
 */
export const mayCancelStop = ({
  user,
  isWholesale,
  businessRoleId,
  allowedTo,
  requestedByUserId,
  policyBrokerageFirmId,
}: {
  user: { isSuperUser?: boolean; userId?: string; brokerageFirmId?: string };
  isWholesale?: boolean;
  businessRoleId?: string;
  allowedTo?: string[];
  requestedByUserId?: string;
  policyBrokerageFirmId?: string;
}): boolean => {
  if (user.isSuperUser || isWholesale) {
    return true;
  }

  if (allowedTo?.length) {
    return !!businessRoleId && allowedTo.includes(businessRoleId);
  }

  return (
    (!!user.userId && user.userId === requestedByUserId) ||
    (!!user.brokerageFirmId && user.brokerageFirmId === policyBrokerageFirmId)
  );
};

export type StopRowAction =
  | { kind: 'resume'; refused: boolean }
  | { kind: 'cancel'; state: 'scheduled' | 'awaitingValidation' }
  | { kind: 'none' };

/**
 * What the events timeline offers on a stop row. Only the open stop slot is actionable, and a
 * pending stop without `executesAt` predates the branch model: it cannot be called off.
 */
export const stopRowAction = ({
  branch,
  branches,
  baseStop,
}: {
  branch: StopBranch;
  branches: StopBranch[] | undefined;
  baseStop: { executesAt?: string } | undefined;
}): StopRowAction => {
  if (branch.branchType !== 'STOP' || !branch.id || openStopBranch(branches)?.id !== branch.id) {
    return { kind: 'none' };
  }

  const state = stopState({ branch, baseEchoesStop: !!baseStop });

  if (state === 'draft') {
    return { kind: 'resume', refused: !!findStopRefusal(branch.id, branches) };
  }

  if (state === 'scheduled' && !baseStop?.executesAt) {
    return { kind: 'none' };
  }

  return { kind: 'cancel', state };
};

/** The backend names broker requesters only, so a role without a name is the customer's own. */
export const isStopRequestedByCustomer = (requestedBy?: {
  businessRoleId?: string;
  name?: string;
}): boolean => !!requestedBy?.businessRoleId && !requestedBy.name;
